import { useState } from 'react';
import { BASEMENT_FINISH_ORDER, BASEMENT_TYPE_ORDER, type PropertyType } from '../../domain/inspection';
import {
  MAX_INSTANCES,
  ROOM_PRESETS,
  defaultHousePlan,
  planCounts,
  planIsEmpty,
  withAddedFloor,
  withBasementFinish,
  withBasementType,
  withExtraRoom,
  withOtherArea,
  withRoomCount,
  withSpaceCount,
  withoutExtraRoom,
  withoutFloor,
  withoutOtherArea,
  type HousePlan
} from '../../domain/housePlan';
import { useT } from '../../i18n/i18n';
import { basementFinishLabel, basementTypeLabel, floorLabelFor, labelWithTag, layoutRowsFor, planSummaryFor, propertyTypeLabel, roomLabel, spaceLabel, type LayoutRow } from '../../i18n/labels';
import { ActionSheet, Button, Chip, CountStepper, Field, IconButton, SectionEyebrow, SheetShell, TextInput, type ActionSheetItem } from '../../ui/components';

// Property Layout on the New Inspection form (Owner task book 2026-09-04 Round 2 §E): a compact card
// — one summary line (未设置 until something is set), one row per floor / basement / garage / attic /
// custom area with a small edit control on the RIGHT, and a compact + in the header that adds a
// floor, basement, garage, attic or other area. No giant edit button. Basement space type and finish
// state stay two separate facts behind the basement row's editor, never in the creation flow itself.
// Owner-selected design 2026-09-14 (Figma 191:7, step 3): grey rows 「一楼（客厅 · 餐厅 …）  编辑」,
// and — while the layout is still empty and a property type is chosen — a tinted suggestion block
// 「按独立屋建议：2 层 · 20 个区域。」 with 使用建议布局 / 稍后调整. Still one explicit tap; nothing is seeded.
type Editing = { kind: 'floor'; index: number } | { kind: 'basement' } | { kind: 'garage' } | { kind: 'attic' } | { kind: 'other'; index: number } | null;

export function PropertyLayoutCard(props: { plan: HousePlan; onChange: (plan: HousePlan) => void; propertyType: PropertyType | null; embedded?: boolean; testId?: string }) {
  const tr = useT();
  const { t, tp, locale } = tr;
  const { plan, onChange } = props;
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const rows = layoutRowsFor(tr, plan);
  const summary = planSummaryFor(tr, plan);
  const counts = planCounts(plan);
  const empty = planIsEmpty(plan);
  const suggestedPlan = empty && props.propertyType !== null && !suggestionDismissed ? defaultHousePlan(props.propertyType) : null;

  function open(row: LayoutRow) {
    switch (row.kind) {
      case 'floor':
        setEditing({ kind: 'floor', index: row.index });
        return;
      case 'basement':
        setEditing({ kind: 'basement' });
        return;
      case 'garage':
        setEditing({ kind: 'garage' });
        return;
      case 'attic':
        setEditing({ kind: 'attic' });
        return;
      case 'other':
        setEditing({ kind: 'other', index: row.index });
        return;
    }
  }

  const addItems: ActionSheetItem[] = [
    {
      key: 'floor',
      label: t('layout.addFloor'),
      detail: floorLabelFor(t, plan.floors.length + 1),
      icon: 'stairs',
      onSelect: () => {
        const next = withAddedFloor(plan);
        onChange(next);
        setAddOpen(false);
        setEditing({ kind: 'floor', index: next.floors.length - 1 });
      },
      testId: 'layout-add-floor'
    },
    {
      key: 'basement',
      label: t('layout.addBasement'),
      icon: 'foundation',
      disabled: plan.basement.type !== null,
      onSelect: () => {
        setAddOpen(false);
        setEditing({ kind: 'basement' });
      },
      testId: 'layout-add-basement'
    },
    {
      key: 'garage',
      label: t('layout.addGarage'),
      icon: 'garage',
      onSelect: () => {
        onChange(plan.spaces.garage === 0 && plan.spaces.detachedGarage === 0 && plan.spaces.carport === 0 ? withSpaceCount(plan, 'garage', 1) : plan);
        setAddOpen(false);
        setEditing({ kind: 'garage' });
      },
      testId: 'layout-add-garage'
    },
    {
      key: 'attic',
      label: t('layout.addAttic'),
      icon: 'layers',
      disabled: plan.spaces.attic > 0,
      onSelect: () => {
        onChange(withSpaceCount(plan, 'attic', 1));
        setAddOpen(false);
      },
      testId: 'layout-add-attic'
    },
    {
      key: 'other',
      label: t('layout.addOther'),
      icon: 'category',
      onSelect: () => {
        setAddOpen(false);
        setEditing({ kind: 'other', index: -1 });
      },
      testId: 'layout-add-other'
    },
    ...(empty && props.propertyType !== null
      ? [
          {
            key: 'suggested',
            label: t('layout.useSuggestedFor', { type: propertyTypeLabel(t, props.propertyType) }),
            detail: t('layout.suggestedDetail'),
            icon: 'auto_awesome_mosaic' as const,
            onSelect: () => {
              onChange(defaultHousePlan(props.propertyType ?? 'singleFamily'));
              setAddOpen(false);
            },
            testId: 'layout-use-suggested'
          }
        ]
      : [])
  ];

  return (
    <section className={`layout-card ${props.embedded ? 'layout-card-embedded' : 'card'}`} data-testid={props.testId ?? 'new-layout'}>
      <div className="card-head">
        <p className="card-title layout-card-title">{t('layout.title')}</p>
        <div className="card-head-right">
          <IconButton icon="add" label={t('layout.addMenu')} onClick={() => setAddOpen(true)} tone="primary" testId="layout-add" />
        </div>
      </div>
      <p className="plan-summary-main" data-testid="plan-summary" data-empty={summary.length === 0 ? 'true' : 'false'}>
        {summary.length > 0 ? summary : t('common.notSet')}
      </p>
      {rows.length > 0 ? (
        <div className="form-rows layout-rows" data-testid="layout-rows">
          {rows.map((row) => (
            <div className="form-row layout-row" key={row.key} data-testid="layout-row" data-row-kind={row.kind} data-name={row.title}>
              <span className="form-row-label layout-row-text">{row.summary.length > 0 ? labelWithTag(locale, row.title, row.summary) : row.title}</span>
              <button type="button" className="form-row-action" onClick={() => open(row)} aria-label={t('layout.rowEdit', { name: row.title })} data-testid="layout-row-edit">
                {t('common.edit')}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="card-note" data-testid="layout-empty">
          {t('layout.emptyHint')}
        </p>
      )}
      {suggestedPlan ? (
        <div className="layout-suggested" data-testid="layout-suggested">
          <p className="layout-suggested-title">
            {t('layout.suggestedBody', {
              type: propertyTypeLabel(t, props.propertyType),
              floors: tp('plan.floorsCount', suggestedPlan.floors.length),
              areas: tp('plan.areasCount', planCounts(suggestedPlan).areas)
            })}
          </p>
          <div className="layout-suggested-links">
            <button type="button" className="link-inline" onClick={() => onChange(suggestedPlan)} data-testid="layout-suggested-apply">
              {t('layout.useSuggested')}
            </button>
            <button type="button" className="link-inline" onClick={() => setSuggestionDismissed(true)} data-testid="layout-suggested-later">
              {t('layout.adjustLater')}
            </button>
          </div>
        </div>
      ) : null}
      {/* Android acceptance 2026-10-21 (issue 3): with no layout yet only the whole-house systems are
          created — say so, so the count does not read as contradicting the suggestion above it. */}
      <p className="plan-summary-sub" data-testid="plan-creates" data-empty={empty ? 'true' : 'false'}>
        {empty ? t('plan.emptyCreatesBefore') : t('plan.createsBefore')} <strong data-testid="plan-area-count">{counts.areas}</strong>
        {empty ? t('plan.emptyCreatesAfter') : tp('plan.createsAfter', counts.areas)}
      </p>

      {addOpen ? <ActionSheet title={t('layout.addMenu')} onClose={() => setAddOpen(false)} items={addItems} testId="layout-add-sheet" /> : null}
      {editing ? <PlanRowEditorSheet editing={editing} plan={plan} onChange={onChange} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

// The per-row editor (Round 2 §E rule 1): opened from the row's own edit control, never from a
// giant standalone button. Each kind edits only its own facts; Remove takes the row away.
function PlanRowEditorSheet(props: { editing: NonNullable<Editing>; plan: HousePlan; onChange: (plan: HousePlan) => void; onClose: () => void }) {
  const tr = useT();
  const { t } = tr;
  const { editing, plan, onChange } = props;
  const [extra, setExtra] = useState('');
  const [otherName, setOtherName] = useState(editing.kind === 'other' && editing.index >= 0 ? (plan.others[editing.index] ?? '') : '');
  const [basementType, setBasementType] = useState(plan.basement.type);
  const [basementFinish, setBasementFinish] = useState(plan.basement.finish);

  const done = (
    <Button onClick={props.onClose} testId="layout-row-done">
      {t('shell.done')}
    </Button>
  );

  if (editing.kind === 'floor') {
    const floor = plan.floors[editing.index];
    if (!floor) return null;
    const floorNo = editing.index + 1;
    return (
      <SheetShell
        title={floorLabelFor(t, floorNo)}
        subtitle={t('layout.floorEditSubtitle')}
        onClose={props.onClose}
        testId="layout-floor-sheet"
        footer={
          <>
            {done}
            <Button
              variant="danger"
              size="small"
              onClick={() => {
                onChange(withoutFloor(plan, editing.index));
                props.onClose();
              }}
              testId="layout-remove-floor"
            >
              {t('layout.removeFloor')}
            </Button>
          </>
        }
      >
        {ROOM_PRESETS.map((preset) => (
          <div className="plan-row" key={preset.key}>
            <span className="plan-row-label">{roomLabel(t, preset.key)}</span>
            <CountStepper value={floor.rooms[preset.key]} min={0} max={MAX_INSTANCES} label={roomLabel(t, preset.key)} onChange={(n) => onChange(withRoomCount(plan, editing.index, preset.key, n))} testId={`plan-floor-${floorNo}-${preset.key}`} />
          </div>
        ))}
        {floor.extras.length > 0 ? (
          <div className="extra-list">
            {floor.extras.map((name, i) => (
              <button key={`${name}-${i}`} type="button" className="chip chip-active" aria-label={t('plan.remove', { name })} onClick={() => onChange(withoutExtraRoom(plan, editing.index, i))}>
                {name} ×
              </button>
            ))}
          </div>
        ) : null}
        <div className="extra-add">
          <div className="extra-add-input">
            <TextInput value={extra} onChange={setExtra} placeholder={t('plan.extraPlaceholder')} testId={`plan-floor-${floorNo}-extra`} />
          </div>
          <Button
            variant="secondary"
            small
            block={false}
            onClick={() => {
              const next = withExtraRoom(plan, editing.index, extra);
              if (next !== plan) {
                onChange(next);
                setExtra('');
              }
            }}
            disabled={extra.trim().length === 0}
            testId={`plan-floor-${floorNo}-extra-add`}
          >
            {t('common.add')}
          </Button>
        </div>
      </SheetShell>
    );
  }

  if (editing.kind === 'basement') {
    const commit = () => {
      onChange(withBasementFinish(withBasementType(plan, basementType), basementType === 'none' || basementType === 'slab' ? null : basementFinish));
      props.onClose();
    };
    return (
      <SheetShell
        title={spaceLabel(t, 'basement')}
        subtitle={t('layout.basementBody')}
        onClose={props.onClose}
        testId="layout-basement-sheet"
        footer={
          <>
            <Button onClick={commit} disabled={basementType === null} testId="layout-row-done">
              {t('shell.done')}
            </Button>
            {plan.basement.type !== null || plan.basement.finish !== null ? (
              <Button
                variant="danger"
                size="small"
                onClick={() => {
                  onChange(withBasementFinish(withBasementType(plan, null), null));
                  props.onClose();
                }}
                testId="layout-remove-basement"
              >
                {t('layout.removeRow')}
              </Button>
            ) : null}
          </>
        }
      >
        <SectionEyebrow tone="primary">{t('layout.basementType')}</SectionEyebrow>
        <div className="chips">
          {BASEMENT_TYPE_ORDER.map((type) => (
            <Chip key={type} label={basementTypeLabel(t, type)} selected={basementType === type} onClick={() => setBasementType(type)} testId={`basement-type-${type}`} />
          ))}
        </div>
        {basementType !== null && basementType !== 'none' && basementType !== 'slab' ? (
          <>
            <SectionEyebrow tone="primary">{t('layout.basementFinish')}</SectionEyebrow>
            <div className="chips">
              {BASEMENT_FINISH_ORDER.map((finish) => (
                <Chip key={finish} label={basementFinishLabel(t, finish)} selected={basementFinish === finish} onClick={() => setBasementFinish(basementFinish === finish ? null : finish)} testId={`basement-finish-${finish}`} />
              ))}
            </div>
            <p className="card-note">{t('layout.finishOptional')}</p>
          </>
        ) : null}
      </SheetShell>
    );
  }

  if (editing.kind === 'garage') {
    return (
      <SheetShell
        title={spaceLabel(t, 'garage')}
        onClose={props.onClose}
        testId="layout-garage-sheet"
        footer={
          <>
            {done}
            <Button
              variant="danger"
              size="small"
              onClick={() => {
                onChange(withSpaceCount(withSpaceCount(withSpaceCount(plan, 'garage', 0), 'detachedGarage', 0), 'carport', 0));
                props.onClose();
              }}
              testId="layout-remove-garage"
            >
              {t('layout.removeRow')}
            </Button>
          </>
        }
      >
        <div className="plan-row">
          <span className="plan-row-label">{t('layout.garageAttached')}</span>
          <CountStepper value={plan.spaces.garage} min={0} max={MAX_INSTANCES} label={t('layout.garageAttached')} onChange={(n) => onChange(withSpaceCount(plan, 'garage', n))} testId="plan-space-garage" />
        </div>
        <div className="plan-row">
          <span className="plan-row-label">{t('layout.garageDetached')}</span>
          <CountStepper value={plan.spaces.detachedGarage} min={0} max={MAX_INSTANCES} label={t('layout.garageDetached')} onChange={(n) => onChange(withSpaceCount(plan, 'detachedGarage', n))} testId="plan-space-detachedGarage" />
        </div>
        <div className="plan-row">
          <span className="plan-row-label">{t('layout.garageCarport')}</span>
          <CountStepper value={plan.spaces.carport} min={0} max={MAX_INSTANCES} label={t('layout.garageCarport')} onChange={(n) => onChange(withSpaceCount(plan, 'carport', n))} testId="plan-space-carport" />
        </div>
      </SheetShell>
    );
  }

  if (editing.kind === 'attic') {
    return (
      <SheetShell
        title={spaceLabel(t, 'attic')}
        onClose={props.onClose}
        testId="layout-attic-sheet"
        footer={
          <>
            {done}
            <Button
              variant="danger"
              size="small"
              onClick={() => {
                onChange(withSpaceCount(plan, 'attic', 0));
                props.onClose();
              }}
              testId="layout-remove-attic"
            >
              {t('layout.removeRow')}
            </Button>
          </>
        }
      >
        <p className="card-note">{t('layout.atticBody')}</p>
      </SheetShell>
    );
  }

  const isNew = editing.index < 0;
  return (
    <SheetShell
      title={t('layout.addOther')}
      onClose={props.onClose}
      testId="layout-other-sheet"
      footer={
        <>
          <Button
            onClick={() => {
              const name = otherName.trim();
              if (name.length === 0) return;
              if (isNew) onChange(withOtherArea(plan, name));
              else onChange(withOtherArea(withoutOtherArea(plan, editing.index), name));
              props.onClose();
            }}
            disabled={otherName.trim().length === 0}
            testId="layout-row-done"
          >
            {isNew ? t('common.add') : t('shell.done')}
          </Button>
          {!isNew ? (
            <Button
              variant="danger"
              size="small"
              onClick={() => {
                onChange(withoutOtherArea(plan, editing.index));
                props.onClose();
              }}
              testId="layout-remove-other"
            >
              {t('layout.removeRow')}
            </Button>
          ) : null}
        </>
      }
    >
      <Field label={t('layout.otherName')} required htmlFor="layout-other-name">
        <TextInput id="layout-other-name" value={otherName} onChange={setOtherName} placeholder={t('layout.otherPlaceholder')} testId="layout-other-name" />
      </Field>
    </SheetShell>
  );
}
